import React from 'react';
import { impactData } from '../data/mock';
import { DollarSign, Users, Heart, TrendingUp, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';

export const ImpactDashboard = () => {
  return (
    <section id="impact" className="py-24 bg-gradient-to-b from-white via-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-playfair text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Impact & Transparency
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-4">
            Every donation is traceable. Every life is accountable.
          </p>
          <div className="flex items-center justify-center gap-2 text-amber-700">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Powered by The Dot Protocol Blockchain</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* Total Donations */}
          <Card className="bg-white border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-gray-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-amber-600" />
                </div>
                Total Donations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-amber-600">
                ${impactData.totalDonations.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600 mt-2">Raised since inception</p>
            </CardContent>
          </Card>

          {/* Children Supported */}
          <Card className="bg-white border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-gray-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-amber-600" />
                </div>
                Children Supported
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-amber-600">{impactData.childrenSupported}</p>
              <p className="text-sm text-gray-600 mt-2">Living in Kibouor homes</p>
            </CardContent>
          </Card>

          {/* Elderly Supported */}
          <Card className="bg-white border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-gray-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-amber-600" />
                </div>
                Elderly Supported
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-amber-600">{impactData.elderlySupported}</p>
              <p className="text-sm text-gray-600 mt-2">Finding family again</p>
            </CardContent>
          </Card>
        </div>

        {/* Fund Allocation */}
        <div className="bg-white rounded-2xl p-8 mb-16 border-2 border-amber-200 shadow-lg">
          <h3 className="font-playfair text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <TrendingUp className="h-8 w-8 text-amber-600" />
            How Your Donations Are Used
          </h3>
          <div className="space-y-6">
            {impactData.allocation.map((item) => (
              <div key={item.category}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-lg font-medium text-gray-900">{item.category}</span>
                  <div className="text-right">
                    <span className="text-amber-600 font-bold text-xl">{item.percentage}%</span>
                    <span className="text-gray-600 text-sm ml-2">
                      (${item.amount.toLocaleString()})
                    </span>
                  </div>
                </div>
                <Progress value={item.percentage} className="h-3 bg-amber-100" />
              </div>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl p-8 border-2 border-amber-200 shadow-lg">
          <h3 className="font-playfair text-3xl font-bold mb-6 text-gray-900">Recent Verified Transactions</h3>
          <div className="space-y-4">
            {impactData.recentTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex justify-between items-center p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors duration-200 border border-amber-200"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-mono text-sm text-gray-700">{transaction.id}</p>
                    <p className="text-xs text-gray-600">{transaction.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-amber-600">
                    ${transaction.amount.toLocaleString()}
                  </p>
                  <p className="text-xs text-green-600 font-medium">Verified on Blockchain</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-600 mt-6">
            All transactions are recorded on the blockchain for complete transparency
          </p>
        </div>
      </div>
    </section>
  );
};